import React, { useEffect, useState, useRef } from "react";
import {
  uploadEmpDocument,
  downloadEmpDocument,
  getAllDocuments,
} from "../../../../../api/onboarddeboard";
import { toast } from "react-toastify";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";

const DocumentDetails = ({
  userInfoId,
  employeeDocuments = [],
  onDocumentUploaded,
}) => {
  const token = localStorage.getItem("token");
  const [allDocuments, setAllDocuments] = useState([]);
  const [uploading, setUploading] = useState({});
  const [uploaded, setUploaded]   = useState({});
  const fileInputRefs = useRef({});

  // ── Load ALL document types from API (GET /documents) ─────────────────────
  // This is the master list — whatever is in your database shows here
  useEffect(() => {
    getAllDocuments(token)
      .then((res) => {
        console.log("Documents from API:", res.data); // debug: check your console
        setAllDocuments(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to load documents:", err);
        toast.error("Failed to load document types from server");
      });
  }, [token]);

  // ── Map already-uploaded docs by documentName ─────────────────────────────
  useEffect(() => {
    const map = {};
    (employeeDocuments || []).forEach((doc) => {
      if (doc.documentName) map[doc.documentName] = doc;
    });
    setUploaded(map);
  }, [employeeDocuments]);

  // ── Trigger hidden file input ─────────────────────────────────────────────
  const handleUploadClick = (docLabel) => {
    if (fileInputRefs.current[docLabel]) {
      fileInputRefs.current[docLabel].value = "";
      fileInputRefs.current[docLabel].click();
    }
  };

  // ── File selected → POST to /emp-documents/upload ────────────────────────
  // doc = one item from allDocuments (has doc.id and doc.documentName)
  const MAX_FILE_SIZE_MB = 1;

  const handleFileSelect = async (e, doc) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reject oversized files before even trying to upload
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File is too large. Please upload a file under ${MAX_FILE_SIZE_MB}MB.`);
      e.target.value = "";
      return;
    }

    // userInfoId must exist — employee must be saved first
    if (userInfoId === null || userInfoId === undefined) {
      toast.error("Please save the employee first before uploading documents.");
      return;
    }

    // doc.id is the real database ID from your /documents API
    if (!doc || !doc.id) {
      toast.error("Document type not found. Please refresh and try again.");
      return;
    }

    const formData = new FormData();
    formData.append("userInfoId",      Number(userInfoId));
    formData.append("documentId",      Number(doc.id));       // ← real DB id
    formData.append("documentDetails", "");
    formData.append("file",            file);

    const docLabel = doc.documentName;
    setUploading((prev) => ({ ...prev, [docLabel]: true }));

    try {
      const res = await uploadEmpDocument(formData, token);
      console.log("Upload response:", res.data); // debug

      const newDoc = {
        empDocumentId: res.data.id,
        documentName:  res.data.document?.documentName || docLabel,
        fileName:      res.data.fileName,
      };

      setUploaded((prev) => ({ ...prev, [docLabel]: newDoc }));
      if (typeof onDocumentUploaded === "function") onDocumentUploaded(newDoc);
      toast.success(`${docLabel} uploaded successfully`);
    } catch (err) {
      console.error("Upload error:", err?.response?.data || err);
      const msg = err?.response?.data?.message || err?.response?.data || `Failed to upload ${docLabel}`;
      toast.error(typeof msg === "string" ? msg : `Failed to upload ${docLabel}`);
    } finally {
      setUploading((prev) => ({ ...prev, [docLabel]: false }));
    }
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownload = async (doc) => {
    if (!doc?.empDocumentId) { toast.error("Document ID not found"); return; }
    try {
      const res  = await downloadEmpDocument(doc.empDocumentId, token);
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", doc.fileName || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Download failed");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20, marginTop: 2 }}>
        Upload required documents for the employee record.
      </p>

      {/* Warning if employee not yet saved */}
      {!userInfoId && (
        <div style={{
          background: "#fff7ed", border: "1.5px solid #fed7aa",
          borderRadius: 8, padding: "10px 16px", marginBottom: 16,
          fontSize: 13, color: "#9a3412", fontWeight: 500,
        }}>
          ⚠️ Please click <strong>Save Employee</strong> first — then you can upload documents.
        </div>
      )}

      {/* Loading state */}
      {allDocuments.length === 0 ? (
        <div style={{ textAlign: "center", padding: 30, color: "#9ca3af", fontSize: 13 }}>
          Loading document types from server…
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Render ALL documents from your database — not a hardcoded list */}
          {allDocuments.map((doc) => {
            const docLabel   = doc.documentName;
            const isUploaded = !!uploaded[docLabel];
            const isLoading  = !!uploading[docLabel];

            return (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 10,
                  padding: "14px 18px",
                  background: "#fff",
                }}
              >
                {/* Left: name + status */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1a202c" }}>
                    {docLabel}
                  </div>
                  {isUploaded ? (
                    <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2, fontWeight: 500 }}>
                      ✓ Uploaded — {uploaded[docLabel].fileName}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                      {doc.required || doc.isMandatory ? "Required" : "Optional"}
                    </div>
                  )}
                </div>

                {/* Right: download + upload buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {isUploaded && (
                    <button
                      onClick={() => handleDownload(uploaded[docLabel])}
                      title="Download"
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#3b5fc0", display: "flex", alignItems: "center", padding: 4,
                      }}
                    >
                      <CloudDownloadRoundedIcon style={{ fontSize: 22 }} />
                    </button>
                  )}

                  <button
                    onClick={() => handleUploadClick(docLabel)}
                    disabled={isLoading}
                    style={{
                      padding: "8px 20px", borderRadius: 8,
                      border: "1.5px solid #d1d5db", background: "#fff",
                      color: "#374151", fontWeight: 600, fontSize: 13,
                      cursor: isLoading ? "not-allowed" : "pointer",
                      minWidth: 80, whiteSpace: "nowrap",
                    }}
                  >
                    {isLoading ? "Uploading..." : isUploaded ? "Re-upload" : "Upload"}
                  </button>

                  {/* Hidden file input — onChange passes full doc object */}
                  <input
                    type="file"
                    ref={(el) => (fileInputRefs.current[docLabel] = el)}
                    style={{ display: "none" }}
                    onChange={(e) => handleFileSelect(e, doc)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default DocumentDetails;